import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { OpenInNewOutlined } from "@mui/icons-material";
import QueryBuilderOutlinedIcon from "@mui/icons-material/QueryBuilderOutlined";
import { Box, Button, Dialog, DialogActions, DialogContent, Stack, Typography } from "@mui/material";
import { EnhancedDialogTitle, LinkButton } from "Common/Components";
import type { INotification, INotificationResource } from "Common/Interfaces";

dayjs.extend(utc);

interface INotificationDetailPopup {
  open: boolean;
  notification: INotification | null;
  handleClose: () => void;
}

const groupResourcesByType = (resources: INotificationResource[]): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  for (const resource of resources) {
    if (!resource.type) continue;
    const existing = map.get(resource.type) ?? [];
    if (resource.value) existing.push(resource.value);
    map.set(resource.type, existing);
  }
  return map;
};

const formatCreatedDate = (createdAt?: string) =>
  createdAt ? dayjs.utc(createdAt).local().format("MM/DD/YYYY, h:mm A") : "";

export const NotificationDetailPopup = (props: INotificationDetailPopup) => {
  const { open, notification, handleClose } = props;
  const groupedResources = notification?.resources?.length ? groupResourcesByType(notification.resources) : null;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <EnhancedDialogTitle title={notification?.title ?? ""} onClose={handleClose} disableClose={false} sx={{ pb: 0 }} />

      <DialogContent>
        <Stack spacing={2}>
          {notification?.createdAt && (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "text.secondary" }}>
              <QueryBuilderOutlinedIcon sx={{ fontSize: 12, lineHeight: 1 }} />
              <Typography variant="body2" sx={{ alignItems: "center", display: "flex", lineHeight: 1 }}>
                {formatCreatedDate(notification.createdAt)}
              </Typography>
            </Stack>
          )}

          {/* Main message */}
          <Typography variant="body1" sx={{ overflowWrap: "anywhere", whiteSpace: "pre-line", wordBreak: "break-word" }}>
            {notification?.message}
          </Typography>
          {/* Resource sections — grouped by type in one card; bullet points when type has multiple values */}
          {groupedResources && (
            <Stack
              spacing={2}
              sx={(theme) => ({
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[200]}`,
                p: 2,
              })}
            >
              {Array.from(groupedResources).map(([type, values]) => (
                <Stack key={type} spacing={0.5}>
                  <Typography sx={{ fontWeight: "fontWeightSemiBold" }}>
                    {values.length > 1 ? `${type}(s)` : type} :
                  </Typography>
                  {values.length === 1 ? (
                    <Typography color="text.secondary" sx={{ wordBreak: "break-word" }}>
                      {values[0]}
                    </Typography>
                  ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2.5, "& li:not(:last-child)": { mb: 1 } }}>
                      {values.map((value) => (
                        <Box key={value} component="li">
                          <Typography color="text.secondary" sx={{ wordBreak: "break-word" }}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
          {/* Deep link */}
          {notification?.deepLink && (
            <LinkButton
              component={"a"}
              href={notification.deepLink}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewOutlined fontSize="small" />}
            >
              Click here for more details
            </LinkButton>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
